import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Building,
  CheckCircle,
  XCircle,
  Search
} from 'lucide-react';
import { Base } from '../../types';
import baseService from '../../services/baseService';
import { toast } from 'react-hot-toast';

interface BaseFormData {
  name: string;
  code: string;
  active: boolean;
}

export default function BaseManagement() {
  const [bases, setBases] = useState<Base[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBase, setEditingBase] = useState<Base | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState<BaseFormData>({
    name: '',
    code: '',
    active: true
  });

  useEffect(() => {
    fetchBases();
  }, []);

  const fetchBases = async () => {
    try {
      const allBases = await baseService.getAllBases();
      setBases(allBases);
    } catch (error) {
      console.error('Error fetching bases:', error);
      toast.error('Failed to load bases');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingBase) {
        await baseService.updateBase(editingBase.id, formData);
        toast.success('Base updated successfully');
      } else {
        await baseService.createBase(formData);
        toast.success('Base created successfully');
      }
      
      setShowModal(false);
      resetForm();
      fetchBases();
    } catch (error: any) {
      toast.error(error.message || 'Operation failed');
    }
  };

  const handleDelete = async (baseId: string) => {
    if (!confirm('Are you sure you want to delete this base? This action cannot be undone if the base has mission history.')) return;

    try {
      await baseService.deleteBase(baseId);
      toast.success('Base deleted successfully');
      fetchBases();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete base');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      active: true
    });
    setEditingBase(null);
  };

  const openEditModal = (base: Base) => {
    setEditingBase(base);
    setFormData({
      name: base.name,
      code: base.code,
      active: base.active
    });
    setShowModal(true);
  };

  const getStatusIcon = (active: boolean) => {
    return active ? (
      <CheckCircle className="h-5 w-5 text-green-500" />
    ) : (
      <XCircle className="h-5 w-5 text-red-500" />
    );
  };

  const filteredBases = bases.filter(base => 
    base.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    base.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-medium text-gray-900">Base Management</h2>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 flex items-center"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Base
        </button>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search bases..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border rounded-md focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>

      {/* Bases Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredBases.map((base) => (
          <div key={base.id} className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="font-medium text-gray-900 flex items-center">
                  <Building className="h-5 w-5 mr-2 text-gray-400" />
                  {base.name}
                </h3>
                <p className="text-sm text-gray-600">Code: {base.code}</p>
              </div>
              <div className="flex items-center">
                {getStatusIcon(base.active)}
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <div>
                <span className="text-gray-500">Status:</span>
                <span className={`ml-2 font-medium ${base.active ? 'text-green-600' : 'text-red-600'}`}>
                  {base.active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Created:</span>
                <span className="ml-2 text-gray-900">
                  {new Date(base.createdAt).toLocaleDateString()}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Updated:</span>
                <span className="ml-2 text-gray-900">
                  {new Date(base.updatedAt).toLocaleDateString()}
                </span>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t flex justify-end space-x-2">
              <button
                onClick={() => openEditModal(base)}
                className="text-indigo-600 hover:text-indigo-900 p-1"
                title="Edit base"
              >
                <Edit2 className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleDelete(base.id)}
                className="text-red-600 hover:text-red-900 p-1"
                title="Delete base"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredBases.length === 0 && !loading && (
        <div className="text-center py-8">
          <Building className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No bases found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm ? 'Try adjusting your search terms.' : 'Get started by creating a new base.'}
          </p>
        </div>
      )}

      {/* Base Form Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-medium mb-4">
              {editingBase ? 'Edit Base' : 'Add New Base'}
            </h3>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Base Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full border rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Vancouver Base"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Base Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    className="mt-1 block w-full border rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="VAN"
                    maxLength={10}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Short code used for identification (e.g., VAN, YVR, etc.)
                  </p>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                  <p className="mt-1 text-xs text-gray-500">
                    Only active bases will appear in mission dropdowns
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  {editingBase ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}